import { ActionIcon, Anchor, Group, Switch, Tooltip } from "@mantine/core"
import { showNotification } from "@mantine/notifications"
import { IconArchive, IconEdit, IconExternalLink } from "@tabler/icons-react"
import {
  createMRTColumnHelper,
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from "mantine-react-table"
import { Link, useLocation } from "react-router-dom"

import { ScheduleDashboardResponses } from "@costory/types/endpoints/scheduledDashboardConfigurations"
import {
  ScheduledDashboardStatus,
  ScheduledPeriod,
} from "@costory/types/prisma-client"

import dayjs from "@costory/shared/dayjs"

import { useUser } from "@costory/front/components/auth/UserContext"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import {
  useDeleteScheduledDashboardConfigurationMutation,
  useGetScheduledDashboardQuery,
  useToggleScheduledDashboardConfigurationStatusMutation,
} from "@costory/front/queries/scheduledDigestConfigurations"
import { getDefaultTableOptions } from "@costory/front/utils/table"

const _ScheduledDashboardList = ({
  configurations,
  onEdit,
}: Props & { configurations: ScheduleDashboardResponses.List }) => {
  const user = useUser()
  const { mutateAsync: deleteConfiguration, isPending: isDeleting } =
    useDeleteScheduledDashboardConfigurationMutation()
  const { mutateAsync: toggleStatus } =
    useToggleScheduledDashboardConfigurationStatusMutation()

  const columnHelper =
    createMRTColumnHelper<ScheduleDashboardResponses.List[0]>()
  const columns: MRT_ColumnDef<ScheduleDashboardResponses.List[0]>[] = [
    columnHelper.accessor("dashboard.name", {
      header: "Dashboard",
      Cell: ({ row }) => {
        const location = useLocation()
        const targetPath = `/dashboards/${row.original.dashboardId}`

        const handleClick = (
          event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        ) => {
          if (location.pathname === targetPath) {
            event.preventDefault()
            showNotification({
              title: "Info",
              message: "You are already on selected saved view",
              color: "green",
            })
          }
        }

        return (
          <Anchor component={Link} to={targetPath} onClick={handleClick}>
            <Group align="center" gap={4}>
              {row.original.dashboard.name}
              <IconExternalLink size={16} />
            </Group>
          </Anchor>
        )
      },
    }),
    columnHelper.accessor("status", {
      header: "Active",
      Cell: ({ row }) => {
        const canEdit = user.isAdmin || row.original.createdById === user.id
        const isEnabled =
          row.original.status === ScheduledDashboardStatus.ACTIVE

        return (
          <Switch
            checked={isEnabled}
            onChange={async () => {
              if (!onEdit) return
              try {
                await toggleStatus(row.original.id)
                showNotification({
                  title: "Success",
                  message: `Scheduled digest successfully ${
                    isEnabled ? "disabled" : "enabled"
                  }`,
                  color: "green",
                })
              } catch {
                showNotification({
                  title: "Error",
                  message: "Failed to update scheduled digest status",
                  color: "red",
                })
              }
            }}
            disabled={!canEdit}
            readOnly={!onEdit}
          />
        )
      },
    }),
    columnHelper.accessor("scheduledPeriod", {
      header: "Next run date",
      Cell: ({ cell, row }) => {
        const period = cell.getValue()
        if (row.original.status === ScheduledDashboardStatus.INACTIVE) {
          return "Disabled"
        }
        const today = dayjs()
        switch (period) {
          case ScheduledPeriod.WEEKLY: {
            const targetDay = 2 // Tuesday
            const daysUntilTarget = (targetDay - today.day() + 7) % 7 || 7
            return today.add(daysUntilTarget, "day").format("MMM DD")
          }
          case ScheduledPeriod.MONTHLY: {
            const targetDay = 3 // 3rd of the month
            const currentMonthTarget = today
              .startOf("month")
              .add(targetDay - 1, "day")

            const nextRunDate =
              today.date() < targetDay
                ? currentMonthTarget
                : today
                    .add(1, "month")
                    .startOf("month")
                    .add(targetDay - 1, "day")

            return nextRunDate.format("MMM DD")
          }
          default:
            return period
        }
      },
    }),
    columnHelper.accessor("createdBy.firstName", {
      header: "Created By",
      Cell: ({ row }) =>
        `${row.original.createdBy.firstName} ${row.original.createdBy.lastName}`,
    }),
    ...(onEdit
      ? [
          columnHelper.display({
            id: "actions",
            header: "Actions",
            Cell: ({ row }) => {
              const canEdit =
                user.isAdmin || row.original.createdById === user.id
              if (!canEdit) return null

              return (
                <Group gap="xs">
                  <Tooltip label="Edit">
                    <ActionIcon onClick={() => onEdit(row.original)}>
                      <IconEdit size={20} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Archive">
                    <ActionIcon
                      onClick={() => {
                        deleteConfiguration(row.original.id, {
                          onSuccess: () => {
                            showNotification({
                              title: "Success",
                              message: "Configuration deleted successfully",
                              color: "green",
                            })
                          },
                          onError: () => {
                            showNotification({
                              title: "Error",
                              message: "Failed to delete configuration",
                              color: "red",
                            })
                          },
                        })
                      }}
                      loading={isDeleting}
                    >
                      <IconArchive size={20} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              )
            },
          }),
        ]
      : []),
  ]

  const table = useMantineReactTable<ScheduleDashboardResponses.List[number]>({
    columns,
    data: configurations,
    ...getDefaultTableOptions(),
  })

  return <MantineReactTable table={table} />
}

type Props = {
  onEdit?: (configuration: ScheduleDashboardResponses.List[0]) => void
}

export const ScheduledDashboardList = ({ onEdit }: Props) => {
  const query = useGetScheduledDashboardQuery()
  return (
    <QueryWrapper query={query} allowEmptyArray>
      {({ data }) => (
        <_ScheduledDashboardList configurations={data} onEdit={onEdit} />
      )}
    </QueryWrapper>
  )
}
